import Cookies from 'js-cookie'
import http, { HTTPError } from '@/utils/http'
import { logError } from '@/utils/logger'
import { ElMessage } from 'element-plus'

const DATA_KEY = `${process.env.NODE_ENV !== 'production' ? `${process.env.NODE_ENV.slice(0, 3)}.` : ''}auth.data`
const SKIP_2FA_KEY = 'auth.skip2fa'
const IS_2FA_AUTH = 'auth.2fa'

const initialState = () => ({
  data: getSavedState(DATA_KEY) || {},
  skip2Fa: getSavedState(SKIP_2FA_KEY) || false,
  is2FAAuth: getSavedState(IS_2FA_AUTH) || false,
  is2FaSetupRequired: false,
  privileges: []
})

export const state = initialState()

export const mutations = {
  RESET (state) {
    saveState(DATA_KEY, {})
    saveState(SKIP_2FA_KEY, false)
    saveState(IS_2FA_AUTH, false)

    const newState = initialState()
    Object.keys(newState).forEach(key => {
      state[key] = newState[key]
    })
  },
  SET_AUTH_DATA (state, data) {
    state.data = data
    saveState(DATA_KEY, data)

    state.is2FaSetupRequired = !data.IsAccountSecretKeyCreated
  },
  SET_PRIVILEGES (state, list = []) {
    state.privileges = list
  },
  SET_SKIP_2FA (state, skip) {
    state.skip2Fa = skip
    saveState(SKIP_2FA_KEY, skip)
  },
  SET_2FA_AUTH (state, isSuccessful) {
    state.is2FAAuth = isSuccessful
    saveState(IS_2FA_AUTH, isSuccessful)
  }
}

export const getters = {
  loggedIn: state => !!state.data.Token && (state.skip2Fa || (!state.skip2Fa && state.is2FAAuth)),
  token: state => state.data.Token,
  username: state => state.data.UserName,
  privileges: state => state.privileges,
  skip2Fa: state => state.skip2Fa,
  is2FaSetupRequired: state => state.is2FaSetupRequired
}

export const actions = {
  logIn ({ commit, getters, dispatch }, { username, password } = {}) {
    if (getters.loggedIn) {
      return true
    }

    return http({
      method: 'post',
      url: `${process.env.VUE_APP_API_URL}login`,
      data: {
        UserName: username,
        Password: password
      }
    }).then((data) => {
      if (!data) {
        ElMessage.warning('Username or password invalid')
        throw new Error('invalid credentials')
      }

      commit('SET_AUTH_DATA', data)
    }).catch(async e => {
      let apiErrorMessage = ''

      try {
        apiErrorMessage = await e.response.json()
      } catch (jsonError) {
        // Response is not JSON, ignore
      }

      // Show user-friendly error messages only
      if (apiErrorMessage === 'Username or password invalid' ||
          apiErrorMessage.startsWith('Account has been blocked')) {
        ElMessage.warning(apiErrorMessage)
      } else if (!(e instanceof HTTPError)) {
        // Only show message if http.js didn't already handle it
        ElMessage.warning('Unexpected error, try again later or contact support')
      }

      throw e
    })
  },
  logInWithJwt ({ commit, getters, dispatch }, token) {
    if (getters.loggedIn) {
      return true
    }

    return http({
      method: 'post',
      url: `${process.env.VUE_APP_API_URL}loginJwt`,
      headers: {
        Authorization: 'Bearer ' + token
      }
    }).then(async (data = {}) => {
      if (!data.Token) {
        const errorMessage = 'error when logging in to Coretix. Please contact support'
        ElMessage.warning(errorMessage)
        throw new Error(errorMessage)
      }
      commit('SET_AUTH_DATA', data)
    }).then(() => dispatch('fetchPrivileges')).then(privileges => {
      const skip2FA = true
      commit('SET_SKIP_2FA', skip2FA)
      return skip2FA
    }).catch(async e => {
      let apiErrorMessage = ''

      try {
        apiErrorMessage = await e.response.json()
      } catch (jsonError) {
        // Response is not JSON, ignore
      }

      // Show user-friendly error messages only
      if (apiErrorMessage.startsWith('Account has been blocked')) {
        ElMessage.warning(apiErrorMessage)
      } else if (!(e instanceof HTTPError)) {
        // Only show message if http.js didn't already handle it
        ElMessage.warning('Unexpected error, try again later or contact support')
      }

      throw e
    })
  },
  fetchPrivileges ({ commit, getters }) {
    return http({
      url: `${process.env.VUE_APP_API_URL}customer/web/priveleges/${getters.token}`
    }).then(data => {
      const privileges = data.map(item => item.Name)
      commit('SET_PRIVILEGES', privileges)

      return privileges
    })
  },
  login2FA ({ commit, getters }, { TwoFactorCode, UserName, Password }) {
    return http({
      method: 'post',
      url: `${process.env.VUE_APP_API_URL}twofactorauth/loginurl`,
      data: {
        TwoFactorCode,
        UserName,
        Password
      }
    })
      .then(async (data = {}) => {
        if (!data.Token) {
          return 0
        }

        commit('SET_AUTH_DATA', data)
        commit('SET_2FA_AUTH', true)
        return 1
      }).catch(async e => {
        let apiErrorMessage = ''

        try {
          apiErrorMessage = await e.response.json()
        } catch (jsonError) {
          // Response is not JSON, ignore
        }

        // Show user-friendly error messages only
        if (apiErrorMessage === 'Username or password invalid' ||
            apiErrorMessage.startsWith('Account has been blocked')) {
          ElMessage.warning(apiErrorMessage)
        } else if (!(e instanceof HTTPError)) {
          // Only show message if http.js didn't already handle it
          ElMessage.warning('Unexpected error, try again later or contact support@itsus.nl')
        }

        throw e
      })
  },
  setup2FA ({ getters }, { UserName, Password }) {
    return http({
      method: 'post',
      url: `${process.env.VUE_APP_API_URL}twofactorauth/create`,
      data: {
        UserName,
        Password
      }
    })
      .then(data => data)
      .catch(e => {
        logError(e, { getters }, 'error setting up 2FA code')
        ElMessage.warning('error setting up 2FA code')
      })
  }
}

// ===
// Private helpers
// ===

function getSavedState (key) {
  try {
    const val = Cookies.get(key)

    if (val) {
      return JSON.parse(val)
    }
  } catch (e) {
    logError(e, { key })
  }

  return null
}

function saveState (key, state) {
  Cookies.set(key, JSON.stringify(state))
}
